import {
  ServerError,
  BadRequestError,
  UnauthenticatedError,
  FormSubmissionError,
  PageNotFoundError
} from '../../core/errors';
import HTTPMethods from '../../enums/http-methods';
import _authHandler from './auth-handler';
import { resolveRequestInfo } from './path-resolver';

const STOCK_HEADERS: HeadersInit = {
  'Content-Type': 'application/json'
};

const isX = (startInclusive: number, endExclusive: number) => {
  return (statusCode: number) =>
    statusCode >= startInclusive && statusCode < endExclusive;
};

const is500 = isX(500, 600);

const _execRequest = async (
  token: string,
  requestInfo: RequestInfo,
  requestInit: RequestInit = {}
): Promise<Response> => {
  const authHeaders = token
    ? {
        Authorization: token
      }
    : {};
  const headers: HeadersInit = {
    ...STOCK_HEADERS,
    ...authHeaders,
    ...requestInit.headers
  };
  const options: RequestInit = {
    method: HTTPMethods.GET,
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    redirect: 'follow',
    referrer: 'no-referrer',
    ...requestInit,
    headers
  };
  if (requestInit.body) {
    options.body = requestInit.body;
  }

  const response = await fetch(resolveRequestInfo(requestInfo), options);
  if (!response.ok) {
    const { status } = response;
    if (is500(status)) {
      throw new ServerError(`error contacting api ${requestInfo}`);
    } else if (status === 400) {
      const responseBody = await response.json();
      const error = new BadRequestError(`error contacting api`, response, responseBody);
      // Attach the parsed response data to the error object for easy access
      (error as any).responseData = responseBody;
      throw error;
    }
    if (status === 403 || status === 401) {
      throw new UnauthenticatedError(`error contacting api ${requestInfo}`);
    } else if (status === 409) {
      const responseBody = await response.json();
      const error = new FormSubmissionError(
        `error contacting api`,
        response,
        responseBody
      );
      // Attach the parsed response data to the error object for easy access
      (error as any).responseData = responseBody;
      throw error;
    } else if (status === 404) {
      throw new PageNotFoundError(`page not found`);
    }
  }
  // automatically reset token if a different one comes through
  const authorization = response.headers.get(`Authorization`);
  if (authorization && _authHandler) {
    _authHandler.token = authorization;
  }

  const location = response.headers.get(`location`);
  if (location && _authHandler) {
    _authHandler.redirectLocation = location;
  }

  return response;
};

const request = async (
  input: RequestInfo,
  init?: RequestInit
): Promise<Response> => {
  const token = _authHandler.token;
  return _execRequest(token, input, init);
};

/**
 * Sends a JSON request to the API and returns the parsed response.
 *
 * @param {RequestInfo} input - The request URL or a Request object.
 * @param {RequestInit} init - Optional request options.
 * @param {boolean} optRequireAuth - Whether the request requires authentication.
 * @returns A Promise that resolves to the parsed JSON response.
 * @throws {UnauthenticatedError} If the request requires authentication but no token is available.
 */
const requestJSON = async <T>(
  input: RequestInfo,
  init: RequestInit = {},
  optRequireAuth = false
): Promise<T> => {
  // This would be *better* if all API requests were authenticated
  // given the testing requirements, this is out of scope for WEBAPP-5217
  if (optRequireAuth && !_authHandler.token) {
    throw new UnauthenticatedError(`no token available`);
  }
  const response = await request(input, init);

  // no content responses result in an error when response.json() is invoked.
  if (response.status === 204) {
    return null;
  }

  return await response.json();
};

export { request, requestJSON };
